import React, { useState, useEffect, useCallback } from 'react';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Print as PrintIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Pagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useNavigate } from 'react-router-dom';
import {
  performanceEventsApi,
  PerformanceEvent,
  PaginatedPerformanceEventsResponse,
} from '../services/performanceEventsService.ts';

const PerformanceEventsListPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const { showError, showSuccess } = useSnackbar();
  const navigate = useNavigate();
  const [performanceEvents, setPerformanceEvents] = useState<PerformanceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PerformanceEvent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user?.type === 'admin';

  const fetchPerformanceEvents = useCallback(async (pageNum: number = 1) => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const response: PaginatedPerformanceEventsResponse =
        await performanceEventsApi.getPerformanceEvents(
          {
            page: pageNum,
            limit,
            ...(isHistoryView ? { type: 'history' as const } : {}),
          },
          sessionToken
        );

      setPerformanceEvents(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch performance events';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, limit, isHistoryView, showError]);

  useEffect(() => {
    fetchPerformanceEvents();
  }, [fetchPerformanceEvents]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchPerformanceEvents(value);
  };

  const handleHistoryToggle = () => {
    setIsHistoryView(!isHistoryView);
    setPage(1);
  };

  const handleEdit = (id: number) => {
    navigate(`/performance-event?id=${id}`);
  };

  const handleCreateNew = () => {
    navigate('/performance-event');
  };

  const handleCopy = (performanceEvent: PerformanceEvent) => {
    navigate('/performance-event', { state: { copyFrom: performanceEvent } });
  };

  const handleDeleteClick = (performanceEvent: PerformanceEvent) => {
    setDeleteTarget(performanceEvent);
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  const handleDeleteOccurrence = async () => {
    if (!deleteTarget || !sessionToken) return;

    setDeleting(true);
    try {
      await performanceEventsApi.deletePerformanceEvent(deleteTarget.id, sessionToken);
      showSuccess('Performance event deleted successfully!');
      setDeleteTarget(null);
      fetchPerformanceEvents(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete performance event';
      showError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSeries = async () => {
    if (!deleteTarget?.seriesId || !sessionToken) return;

    setDeleting(true);
    try {
      const result = await performanceEventsApi.deletePerformanceEventSeries(deleteTarget.seriesId, sessionToken);
      showSuccess(`Deleted all ${result.deletedCount} occurrences of "${deleteTarget.performanceName}"!`);
      setDeleteTarget(null);
      fetchPerformanceEvents(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete the series';
      showError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatOccurrence = (performanceEvent: PerformanceEvent) => {
    if (!performanceEvent.isRecurring) return 'One-time';
    const freq = performanceEvent.recurrenceFrequency ?? '';
    const day = performanceEvent.recurrenceDayOfWeek;
    return day ? `${freq} (${day})` : freq;
  };

  const handlePrint = (performanceEvent: PerformanceEvent) => {
    const html = generatePrintHTML(performanceEvent);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    }
  };

  const generatePrintHTML = (performanceEvent: PerformanceEvent): string => {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>Performance Event - ${formatDate(performanceEvent.eventDate)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background: white; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #1976d2; padding-bottom: 20px; }
          .header h1 { color: #1976d2; font-size: 32px; margin-bottom: 10px; font-weight: bold; }
          .header .address { font-size: 16px; color: #666; margin-top: 10px; }
          .form-row { display: flex; margin-bottom: 15px; gap: 20px; }
          .form-field { flex: 1; }
          .form-field label { font-weight: bold; color: #555; display: block; margin-bottom: 5px; font-size: 14px; }
          .form-field .value { padding: 8px; background-color: #f9f9f9; border: 1px solid #ddd; border-radius: 4px; min-height: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Post Office Pub</h1>
          <div class="address">1 Ray St, North Grafton, MA 01536, United States. Phone: +1 508-839-6106</div>
        </div>
        <div class="form-row">
          <div class="form-field">
            <label>Performance Name</label>
            <div class="value">${performanceEvent.performanceName}</div>
          </div>
          <div class="form-field">
            <label>Performer Name</label>
            <div class="value">${performanceEvent.performerName}</div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-field">
            <label>Date</label>
            <div class="value">${formatDate(performanceEvent.eventDate)}</div>
          </div>
          <div class="form-field">
            <label>Week Day</label>
            <div class="value">${performanceEvent.weekDay}</div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-field">
            <label>Occurrence</label>
            <div class="value">${formatOccurrence(performanceEvent)}</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  if (loading && performanceEvents.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Performance Events
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={isHistoryView ? 'contained' : 'outlined'}
            onClick={handleHistoryToggle}
          >
            History
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchPerformanceEvents(page)}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create New Form
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['Performance Name', 'Performer Name', 'Date', 'Week Day', 'Occurrence', 'Actions'].map((h) => (
                  <TableCell key={h} align={h === 'Actions' ? 'center' : 'left'} sx={{
                    color: '#fff', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.04em',
                    textTransform: 'uppercase', border: '2px solid #7f0000', py: 1.5, px: 1.5, whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {performanceEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                    No performance events found
                  </TableCell>
                </TableRow>
              ) : (
                performanceEvents.map((performanceEvent, idx) => (
                  <TableRow key={performanceEvent.id} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5', '&:hover': { backgroundColor: '#fce4e4' } }}>
                    <TableCell>{performanceEvent.performanceName}</TableCell>
                    <TableCell>{performanceEvent.performerName}</TableCell>
                    <TableCell>{formatDate(performanceEvent.eventDate)}</TableCell>
                    <TableCell>{performanceEvent.weekDay}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatOccurrence(performanceEvent)}
                        color={performanceEvent.isRecurring ? 'primary' : 'default'}
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="secondary" onClick={() => handleEdit(performanceEvent.id)} aria-label="edit" size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton color="default" onClick={() => handlePrint(performanceEvent)} aria-label="print" size="small">
                        <PrintIcon fontSize="small" />
                      </IconButton>
                      <IconButton color="info" onClick={() => handleCopy(performanceEvent)} aria-label="copy" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      {isAdmin && (
                        <IconButton color="error" onClick={() => handleDeleteClick(performanceEvent)} aria-label="delete" size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {performanceEvents.length} of {total} performance event
              {total !== 1 ? 's' : ''}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Performance Event</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.performanceName}"
            {deleteTarget ? ` on ${formatDate(deleteTarget.eventDate)}` : ''}?
            {deleteTarget?.seriesId
              ? ' This is part of a recurring series — choose whether to delete just this date or every occurrence.'
              : ' This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          {deleteTarget?.seriesId && (
            <Button onClick={handleDeleteSeries} color="error" variant="outlined" disabled={deleting}>
              {deleting ? <CircularProgress size={20} /> : 'Delete All Occurrences'}
            </Button>
          )}
          <Button onClick={handleDeleteOccurrence} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : deleteTarget?.seriesId ? 'Delete This Occurrence' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformanceEventsListPage;
